import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sites } from '@cwos/api-client';
import type { sites as SitesNS } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { SettingsCard } from '../settings/common.js';
import { SITES_MANAGED_ELSEWHERE } from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { formatMeters } from './geofenceForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Work sites — the LOCATION LENS over the `location` entity, deliberately NOT
 * a second CRUD.
 *
 * DECISION (design doc §4, directive §32): the `location` entity already has a
 * full management surface at Settings → Sites & locations (SitesSettings.tsx /
 * SiteDetail.tsx over the sites domain — create, address, map position,
 * programs, per-site geofence, retire). Duplicating those forms here would
 * make one entity editable from two screens that could drift apart, so this
 * page answers the question only the location hub asks — WHICH work sites can
 * a punch be verified against, and which have no coverage — and every row
 * links into the real editor. Its one filter narrows to the sites that need
 * attention (no active geofence); nothing here is a control that pretends to
 * write.
 *
 * Reads with `location:read` (the sites list route's own gate), so a read-only
 * holder sees the same honest coverage picture with no write affordances —
 * those live behind `location:manage` in Sites & locations and on Geofences.
 */

/** The sites list route's hard `limit` ceiling (listSitesSchema: max 200). */
const LENS_LIMIT = 200;

function coverageOf(site: SitesNS.Site): 'active' | 'inactive' | 'none' {
  if (!site.geofence) return 'none';
  return site.geofence.status === 'active' ? 'active' : 'inactive';
}

export function LocationSitesPage() {
  const navigate = useNav();
  const [uncoveredOnly, setUncoveredOnly] = useState(false);

  const list = useQuery({
    queryKey: ['sites', 'location-lens'],
    queryFn: () => sites.list({ status: 'all', limit: LENS_LIMIT }),
  });

  const all = list.data?.sites ?? [];
  const operating = all.filter((s) => s.status !== 'retired');
  const covered = operating.filter((s) => coverageOf(s) === 'active');
  const rows = uncoveredOnly ? operating.filter((s) => coverageOf(s) !== 'active') : operating;

  const columns: Column<SitesNS.Site>[] = [
    {
      key: 'name',
      header: 'Work site',
      render: (s) => (
        <span>
          <span style={{ fontWeight: 600 }}>{s.name}</span>
          {s.code ? <span className="rn-muted"> · {s.code}</span> : null}
        </span>
      ),
    },
    { key: 'address', header: 'Address', render: (s) => s.addressSummary ?? '—' },
    {
      key: 'position',
      header: 'Map position',
      render: (s) => (s.latitude != null && s.longitude != null ? 'Set' : 'Not set'),
    },
    {
      key: 'geofence',
      header: 'Geofence',
      render: (s) => {
        const coverage = coverageOf(s);
        if (coverage === 'active') {
          return (
            <Pill variant="good" dot>
              Within {formatMeters(s.geofence!.radiusMeters)}
            </Pill>
          );
        }
        if (coverage === 'inactive') {
          return (
            <Pill variant="plain" dot>
              Inactive
            </Pill>
          );
        }
        return <span className="rn-muted">None</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <Pill variant={s.status === 'active' ? 'good' : 'plain'} dot>
          {s.status === 'active' ? 'Active' : 'Inactive'}
        </Pill>
      ),
    },
  ];

  return (
    <LocationSettingsShell active="sites">
      <SettingsCard>
        <h2 className="rn-card-title">Where sites are managed</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {SITES_MANAGED_ELSEWHERE}
        </p>
        <p style={{ marginTop: 'var(--space-10)' }}>
          <a
            href="/settings/sites"
            className="rn-link"
            data-testid="manage-sites-link"
            onClick={(e) => {
              e.preventDefault();
              navigate('/settings/sites');
            }}
          >
            Open Sites &amp; locations
          </a>
        </p>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Geofence coverage</h2>
        {list.isLoading ? (
          <SkeletonSet surface="table" rows={4} />
        ) : list.isError && !list.data ? (
          <LoadFailure
            error={list.error}
            message="We couldn’t load work sites just now."
            onRetry={() => void list.refetch()}
          />
        ) : operating.length === 0 ? (
          <SuccessEmpty
            heading="No work sites yet"
            message="Add your first site under Settings → Sites & locations; a geofence can be switched on as part of adding it."
          />
        ) : (
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
            <p className="rn-muted" role="status">
              {covered.length} of {operating.length} operating{' '}
              {operating.length === 1 ? 'site has' : 'sites have'} an active geofence.
              {list.data && list.data.total > LENS_LIMIT
                ? ` Showing the first ${LENS_LIMIT} of ${list.data.total} — search the rest in Sites & locations.`
                : ''}
            </p>
            <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={uncoveredOnly}
                onChange={(e) => setUncoveredOnly(e.target.checked)}
              />
              <span>Only sites without an active geofence</span>
            </label>
            {rows.length === 0 ? (
              <SuccessEmpty
                heading="Every operating site is covered"
                message="Each one has an active geofence for punches to be checked against."
              />
            ) : (
              <TableCard
                columns={columns}
                rows={rows}
                rowKey={(s) => s.id}
                caption="Work sites and their geofence coverage"
                onRowOpen={(s) => navigate(`/settings/sites/${s.id}`)}
              />
            )}
          </div>
        )}
      </SettingsCard>
    </LocationSettingsShell>
  );
}
